import TutorialOverview from "@/components/TutorialOverview";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useNavigate, useLocation } from "react-router";
import { useMemo, useEffect } from "react";
import { useUser } from "@supabase/auth-helpers-react";
import { useTranslation } from "react-i18next";
import { UseMutationResult } from "@tanstack/react-query";
import { MainLayout } from "@/components/layouts/MainLayout";
import { FilterComponent } from "@/components/FilterComponent";
import { useTagPreferences } from "@/hooks/useTagPreferences";
import { useSavedTutorials } from "@/hooks/useSavedTutorials";
import { TagPreferenceType } from "@/lib/supabase";
import { useCategoryStore } from "../stores/use-tutorial-progress";

const Saved = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useUser();
  const { t } = useTranslation();
  const { savedTutorials, isTutorialSaved, isLoading } = useSavedTutorials(
    user?.id
  );
  const { getPlaylistById, resetCurrentSelection } = useCategoryStore();
  const { preferences: tagPreferences, updatePreference: updateTagPreference } =
    useTagPreferences(user?.id ?? "");

  useEffect(() => resetCurrentSelection, []);
  // Check for reset parameter and clear filters if present
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const shouldReset = searchParams.get("reset") === "true";

    if (shouldReset && user?.id) {
      // Reset any active tag preferences
      const specialTags = ["completed", "not interested"];

      // Process all tags that need to be reset
      specialTags.forEach((tagName) => {
        if (tagPreferences.included?.includes(tagName)) {
          // If the tag is selected, deselect it
          // Need to use the correct parameter structure based on the hook implementation
          updateTagPreference.mutate({
            tagName,
            preferenceType: null,
          });
        }
      });

      // Clean up the URL after resetting filters
      navigate("/saved", { replace: true });
    }
  }, [
    location.search,
    tagPreferences,
    updateTagPreference,
    user?.id,
    navigate,
  ]);
  const filteredTutorials = useMemo(() => {
    const preferredPlaylist = getPlaylistById(tagPreferences.included[0]);
    const tutorialsInPlaylist = preferredPlaylist?.tutorials || [];
    return savedTutorials.filter((tutorial) => {
      const matchesTags =
        tagPreferences.included.length === 0 ||
        tutorialsInPlaylist
          .map((t) => t.documentId)
          .includes(tutorial.documentId);

      return matchesTags;
    });
  }, [getPlaylistById, savedTutorials, tagPreferences.included]);

  return (
    <MainLayout>
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-3">
          {/* <FilterComponent
						tagPreferences={tagPreferences}
						updateTagPreference={
							updateTagPreference as UseMutationResult<
								void,
								Error,
								{ tagName: string; preferenceType: TagPreferenceType | null; }
							>
						}
						isLargeScreen
					/> */}
        </div>

        <main className="col-span-12 space-y-4 md:col-span-9 px-2 md:px-0">
          <Button
            variant="ghost"
            className="mb-2 -ml-2 text-muted-foreground hover:text-foreground"
            onClick={() => navigate("/")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t("saved.returnToFeed")}
          </Button>
          <h1 className="text-2xl font-bold">{t("saved.title")}</h1>
          {isLoading ? (
            <div className="grid gap-4 md:grid-cols-2">
              {[1, 2, 3, 4].map((n) => (
                <div
                  key={n}
                  className="animate-pulse space-y-4 rounded-lg bg-card p-6"
                >
                  <div className="h-6 w-3/4 rounded bg-muted" />
                  <div className="h-4 w-1/2 rounded bg-muted" />
                </div>
              ))}
            </div>
          ) : savedTutorials.length === 0 ? (
            <div className="rounded-lg border bg-background p-6 text-center text-muted-foreground">
              {t("saved.noSavedTutorials")}
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {filteredTutorials.map((tutorial) => (
                <TutorialOverview
                  key={tutorial.id}
                  tutorial={{ ...tutorial, url: tutorial.url || "" }}
                  isSaved={isTutorialSaved(tutorial.documentId)}
                />
              ))}
            </div>
          )}
        </main>
      </div>
    </MainLayout>
  );
};

export default Saved;
